import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  Box,
  TableContainer,
  Grid,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  useBreakpointValue,
  VStack,
  Stack,
  Flex,
} from "@chakra-ui/react";
import { SearchIcon, CloseIcon } from "@chakra-ui/icons";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState, useRef } from "react";
import { fetchDonations } from "../api/api";
import TopDonations from "../components/TopDonations";

const SAFFRON = "#E07B1A";
const SAFFRON_DARK = "#854F0B";
const MotionBox = motion(Box);

// Wraps the matching part of a name in a highlighted <mark>, so the
// user can immediately see WHY a result matched their search.
function HighlightedName({ text, query }) {
  if (!query.trim()) return <>{text}</>;

  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const lowerText = text.toLowerCase();

  // Find all matching ranges across every search word, then merge them
  // so overlapping highlights don't render as nested <mark> tags.
  const ranges = [];
  words.forEach((word) => {
    let idx = lowerText.indexOf(word);
    while (idx !== -1) {
      ranges.push([idx, idx + word.length]);
      idx = lowerText.indexOf(word, idx + 1);
    }
  });
  if (ranges.length === 0) return <>{text}</>;

  ranges.sort((a, b) => a[0] - b[0]);
  const merged = [ranges[0]];
  for (const [start, end] of ranges.slice(1)) {
    const last = merged[merged.length - 1];
    if (start <= last[1]) last[1] = Math.max(last[1], end);
    else merged.push([start, end]);
  }

  const parts = [];
  let cursor = 0;
  merged.forEach(([start, end], i) => {
    if (start > cursor)
      parts.push(<span key={`t${i}`}>{text.slice(cursor, start)}</span>);
    parts.push(
      <Box
        as="mark"
        key={`m${i}`}
        bg="orange.200"
        color={SAFFRON_DARK}
        borderRadius="3px"
        px="1px"
      >
        {text.slice(start, end)}
      </Box>,
    );
    cursor = end;
  });
  if (cursor < text.length)
    parts.push(<span key="tail">{text.slice(cursor)}</span>);

  return <>{parts}</>;
}

export default function Donations() {
  const [donors, setDonors] = useState([]);
  const [filteredDonors, setFilteredDonors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const fontSize = useBreakpointValue({ base: "sm", md: "md" });
  const isMobile = useBreakpointValue({ base: true, md: false });

  const scrollRef = useRef(null);
  const isSearching = search.trim().length >= 3;

  useEffect(() => {
    fetchDonations()
      .then((dRes) => {
        const sorted = [...dRes.data].sort((a, b) => a.id - b.id);
        setDonors(sorted);
        setFilteredDonors(sorted);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Word-based search — every typed word must appear somewhere in the
  // donor's full name, in any order (handles "Ravi Kumar" and "Kumar
  // Ravi" matching the same donor). Activates at 3+ characters.
  useEffect(() => {
    const query = search.trim().toLowerCase();

    if (query.length < 3) {
      setFilteredDonors(donors);
      return;
    }

    const queryWords = query.split(/\s+/).filter(Boolean);

    setFilteredDonors(
      donors.filter((d) => {
        const fullName = `${d.donorFirstName || ""} ${d.donorLastName || ""}`
          .toLowerCase()
          .trim();
        return queryWords.every((word) => fullName.includes(word));
      }),
    );
  }, [search, donors]);

  // 🔹 Smooth auto-scroll — now PAUSES while the user is actively
  // searching (3+ characters typed). Previously it kept sliding the
  // list regardless, which fought against the user trying to read
  // their filtered results. It resumes automatically once the search
  // box is cleared.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    // Don't auto-scroll at all while a search is active — let the
    // user read and manually scroll through their filtered matches.
    if (isSearching) return;

    container.scrollTop = container.scrollHeight;

    let scrollStep = 0.3;
    let animationFrameId;

    const scroll = () => {
      if (container.scrollTop <= 0) {
        container.scrollTop = container.scrollHeight;
      } else {
        container.scrollTop -= scrollStep;
      }
      animationFrameId = requestAnimationFrame(scroll);
    };

    animationFrameId = requestAnimationFrame(scroll);

    return () => cancelAnimationFrame(animationFrameId);
  }, [filteredDonors, isMobile, isSearching]);

  if (loading) return <Spinner />;

  const visibleDonors = filteredDonors;

  return (
    <Box maxW={{ base: "100%", md: "1200px" }} mx="auto">
      <Grid
        templateColumns={{ base: "1fr", md: "2fr 1fr" }}
        gap={6}
        px={{ base: 2, md: 4 }}
        py={4}
        alignItems="start"
      >
        {/* Left: Donation Table */}
        <Box>
          <Flex align="center" justify="space-between" mb={2}>
            <Text fontSize="xl" fontWeight="bold">
              Donations
            </Text>
            <AnimatePresence>
              {isSearching && (
                <MotionBox
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                >
                  <Text
                    fontSize="xs"
                    fontWeight="600"
                    color={SAFFRON_DARK}
                    bg="orange.50"
                    px={2}
                    py="2px"
                    borderRadius="full"
                  >
                    {filteredDonors.length} match
                    {filteredDonors.length !== 1 ? "es" : ""}
                  </Text>
                </MotionBox>
              )}
            </AnimatePresence>
          </Flex>

          <Box
            border="1px solid #eee"
            borderRadius="md"
            bg="white"
            overflow="hidden"
            boxShadow={isSearching ? "0 4px 16px rgba(224,123,26,0.1)" : "none"}
            transition="box-shadow 0.2s ease"
          >
            <VStack spacing={2} p={3} align="stretch">
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="orange.300" boxSize={3.5} />
                </InputLeftElement>
                <Input
                  placeholder="Search by donor name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="10px"
                  // Forces 16px regardless of breakpoint — under 16px is
                  // exactly what makes iOS/Android auto-zoom on focus.
                  fontSize="16px"
                  sx={{ fontSize: "16px !important" }}
                  _focus={{
                    borderColor: SAFFRON,
                    boxShadow: `0 0 0 1px ${SAFFRON}`,
                    bg: "white",
                  }}
                  transition="all 0.15s ease"
                />
                {search && (
                  <InputRightElement>
                    <IconButton
                      aria-label="Clear search"
                      icon={<CloseIcon boxSize={2.5} />}
                      size="xs"
                      variant="ghost"
                      color="gray.400"
                      onClick={() => setSearch("")}
                    />
                  </InputRightElement>
                )}
              </InputGroup>
            </VStack>

            {isMobile ? (
              // ---- Mobile View: Card layout with enter/exit animation ----
              <Stack
                ref={scrollRef}
                spacing={3}
                p={3}
                maxH="220px"
                overflowY="auto"
                sx={{
                  WebkitOverflowScrolling: "touch",
                  "::-webkit-scrollbar": { width: "4px" },
                  "::-webkit-scrollbar-thumb": {
                    background: "#ccc",
                    borderRadius: "4px",
                  },
                }}
              >
                {visibleDonors.length === 0 ? (
                  <Box textAlign="center" py={6}>
                    <Text fontSize="24px" mb={1}>
                      🔍
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      No matching donations found.
                    </Text>
                  </Box>
                ) : (
                  <AnimatePresence initial={false}>
                    {visibleDonors.map((d) => (
                      <MotionBox
                        key={d.id}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.18 }}
                        border="1px solid #eee"
                        borderRadius="md"
                        p={3}
                        bg="orange.50"
                        _active={{ transform: "scale(0.98)" }}
                      >
                        <Flex justify="space-between" fontSize="sm">
                          <Text fontWeight="bold">
                            <HighlightedName
                              text={`${d.donorLastName || ""} ${d.donorFirstName || ""}`}
                              query={search}
                            />
                          </Text>
                          <Text color={SAFFRON_DARK} fontWeight="semibold">
                            ₹ {d.amount?.toLocaleString("en-IN")}
                          </Text>
                        </Flex>
                      </MotionBox>
                    ))}
                  </AnimatePresence>
                )}
              </Stack>
            ) : (
              // ---- Desktop/Tablet View: Table layout ----
              <TableContainer
                ref={scrollRef}
                maxH="220px"
                overflowY="auto"
                overflowX="auto"
                borderTop="1px solid #eee"
                borderRadius="md"
                sx={{
                  WebkitOverflowScrolling: "touch",
                  "::-webkit-scrollbar": { width: "4px", height: "4px" },
                  "::-webkit-scrollbar-thumb": {
                    background: "#ccc",
                    borderRadius: "4px",
                  },
                }}
              >
                <Table variant="striped" size="sm" colorScheme="orange">
                  <Thead bg="orange.500" position="sticky" top="0" zIndex={1}>
                    <Tr>
                      <Th color="white" fontSize={fontSize}>
                        Donor
                      </Th>
                      <Th color="white" fontSize={fontSize} isNumeric>
                        Amount (₹)
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredDonors.length === 0 ? (
                      <Tr>
                        <Td
                          colSpan={2}
                          color="gray.500"
                          textAlign="center"
                          py={6}
                        >
                          🔍 No matching donations found.
                        </Td>
                      </Tr>
                    ) : (
                      filteredDonors.map((d) => (
                        <Tr
                          key={d.id}
                          _hover={{ bg: "orange.50" }}
                          transition="background 0.15s ease"
                        >
                          <Td fontSize={fontSize}>
                            <HighlightedName
                              text={`${d.donorLastName || ""} ${d.donorFirstName || ""}`}
                              query={search}
                            />
                          </Td>
                          <Td isNumeric fontSize={fontSize}>
                            {d.amount?.toLocaleString("en-IN")}
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>

        {/* Right: Top Donors */}
        <TopDonations isMobile={isMobile} />
      </Grid>
    </Box>
  );
}
